import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Send, Sparkles, Plus, Trash2, Menu, X, MessageSquare, Wallet, Check, Copy,
} from "lucide-react";
import { apiFetch, api } from "@/lib/api";
import { renderMarkdown } from "@/lib/md";
import FlightResults from "@/components/FlightResults";
import InviteFriendModal from "@/components/InviteFriendModal";
import SubscribeModal from "@/components/SubscribeModal";

const PROMPT_EXAMPLES = [
  "JFK to Tokyo in business, late September 2026, 2 travelers.",
  "Cape Town from JFK in business, March 2026, ±5 days flexible.",
  "Lisbon from EWR in premium economy, first week of May 2026.",
];

function Bubble({ role, children }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[88%] px-3.5 py-2.5 rounded-[12px] text-[13px] leading-relaxed ${
          isUser
            ? "bg-black text-white"
            : "bg-[#fbfaf6] text-neutral-900 border border-black/5"
        }`}
      >
        {children}
      </div>
    </div>
  );
}

// Admin-only: shows the exact search filter the planner used so it can be
// verified. Only rendered when the backend (admin-gated) sends a debug event.
function DebugFilter({ debug }) {
  const q = debug.query || {};
  const ti = debug.tool_input || {};
  const reach = debug.reachable || {};
  const sources = Object.keys(reach).sort();
  return (
    <div className="flex justify-start">
      <div className="max-w-[88%] rounded-[12px] bg-neutral-900 text-neutral-100 px-3.5 py-2.5 text-[11px] font-mono leading-snug overflow-x-auto">
        <div className="text-amber-300 font-semibold tracking-wide mb-1.5 not-italic">SEARCH FILTER · admin</div>
        <div>route: {(q.origin || ti.origin_airport) ?? "—"} → {(q.destination || ti.destination_airport) ?? "—"}</div>
        <div>dates: {(q.start_date || ti.start_date) ?? "—"} … {(q.end_date || ti.end_date) ?? "—"}</div>
        <div>cabin: {(q.cabin ?? ti.cabin) || "any"}</div>
        <div>matched: {debug.total_matched} · shown: {debug.count}</div>
        <div className="mt-1.5 text-neutral-400">reachable programs ({debug.reachable_count}):</div>
        <div className="text-neutral-200 break-words">
          {sources.length === 0
            ? "(none)"
            : sources.map((s) => {
                const v = reach[s];
                if (!v) return s;                       // direct
                if (Array.isArray(v)) return `${s}←${v.join(",")}`;
                return `${s}←${v}`;                     // legacy single-string shape
              }).join(", ")}
        </div>
      </div>
    </div>
  );
}

export default function TripChat() {
  const navigate = useNavigate();
  const { id: paramId } = useParams();

  const [threads, setThreads] = useState([]);
  const [messages, setMessages] = useState([]);
  const [flights, setFlights] = useState([]);
  const [query, setQuery] = useState(null);
  const [running, setRunning] = useState(false);
  const [status, setStatus] = useState(null);
  const [input, setInput] = useState("");
  const [err, setErr] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [serverOk, setServerOk] = useState(null);
  const [railOpen, setRailOpen] = useState(false);
  const [programCount, setProgramCount] = useState(null); // null = unloaded; number once fetched
  const [referrals, setReferrals] = useState(null); // { share_url, qualified_count, referrals: [...] } or null
  const [linkCopied, setLinkCopied] = useState(false);
  const [pulse, setPulse] = useState(false); // brief highlight when results change

  const loadReferrals = useCallback(async () => {
    try {
      const { data } = await api.get("/referrals/me");
      setReferrals(data || null);
    } catch {
      setReferrals(null);
    }
  }, []);

  const copyReferralLink = async () => {
    const url = referrals?.share_url;
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1800);
    } catch {
      /* ignore */
    }
  };

  // Thread id whose data is currently in memory. Lets us skip a redundant
  // hydrate when we navigate to a thread we just created mid-stream.
  const loadedIdRef = useRef(null);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);
  const pulseTimerRef = useRef(null);

  const flashResults = () => {
    setPulse(true);
    clearTimeout(pulseTimerRef.current);
    pulseTimerRef.current = setTimeout(() => setPulse(false), 1300);
  };

  const loadThreads = async () => {
    try {
      const { data } = await api.get("/trips/threads");
      setThreads(data || []);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    api.get("/trips/health").then(({ data }) => setServerOk(data.ok)).catch(() => setServerOk(false));
    loadThreads();
    api
      .get("/programs")
      .then(({ data }) => {
        const rows = Array.isArray(data) ? data : [];
        const active = rows.filter((p) => p.is_active !== false);
        setProgramCount(active.length);
      })
      .catch(() => setProgramCount(0));
    loadReferrals();
    return () => clearTimeout(pulseTimerRef.current);
  }, [loadReferrals]);

  // While the paywall is active, poll /referrals/me every 15s so the
  // footer clears automatically the moment an invitee adds a qualifying
  // program in another tab/session. Also refresh on window focus.
  const paywallActive = !!referrals?.paywall?.active;
  useEffect(() => {
    if (!paywallActive) return;
    const onFocus = () => loadReferrals();
    window.addEventListener("focus", onFocus);
    const t = setInterval(() => loadReferrals(), 15000);
    return () => {
      window.removeEventListener("focus", onFocus);
      clearInterval(t);
    };
  }, [paywallActive, loadReferrals]);

  const hasPrograms = programCount === null ? true : programCount > 0;

  // Hydrate when the route's thread id changes (incl. refresh / direct link).
  useEffect(() => {
    if (paramId && paramId === loadedIdRef.current) return; // already in memory
    if (!paramId) {
      setMessages([]);
      setFlights([]);
      setQuery(null);
      loadedIdRef.current = null;
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/trips/threads/${paramId}`);
        if (cancelled) return;
        // Filter out any legacy paywall pseudo-messages from older threads
        // (we no longer persist them; the live state comes from /referrals/me).
        setMessages(
          (data.messages || [])
            .filter((m) => m.role !== "paywall")
            .map((m) => ({ role: m.role, content: m.content }))
        );
        setFlights(data.flights || []);
        setQuery(data.query || null);
        loadedIdRef.current = paramId;
      } catch {
        if (!cancelled) navigate("/plan", { replace: true });
      }
    })();
    return () => { cancelled = true; };
  }, [paramId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, status]);

  const newTrip = () => {
    if (running) return;
    setRailOpen(false);
    navigate("/plan");
  };

  const deleteThread = async (tid) => {
    try {
      await api.delete(`/trips/threads/${tid}`);
    } catch {
      /* ignore */
    }
    await loadThreads();
    if (tid === paramId) {
      loadedIdRef.current = null;
      navigate("/plan", { replace: true });
    }
  };

  const stop = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setRunning(false);
    setStatus(null);
  };

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || running) return;
    setErr("");
    setInput("");
    setRailOpen(false);

    setMessages((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    setRunning(true);
    setStatus("thinking…");

    const controller = new AbortController();
    abortRef.current = controller;

    let assistantText = "";
    try {
      const resp = await apiFetch("/trips/plan", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ thread_id: paramId || null, prompt: q }),
        signal: controller.signal,
      });
      if (!resp.ok || !resp.body) {
        const t = await resp.text().catch(() => "");
        throw new Error(t || `${resp.status} ${resp.statusText}`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf("\n\n")) !== -1) {
          const evt = buf.slice(0, idx).trim();
          buf = buf.slice(idx + 2);
          if (!evt.startsWith("data:")) continue;
          try {
            const obj = JSON.parse(evt.slice(5).trim());
            if (obj.thread_id && obj.thread_id !== paramId) {
              // A brand-new thread was created server-side. Adopt its id
              // without triggering a hydrate (we already hold the live state).
              loadedIdRef.current = obj.thread_id;
              navigate(`/plan/${obj.thread_id}`, { replace: true });
            }
            if (Array.isArray(obj.flights)) {
              setFlights(obj.flights);
              if (obj.query !== undefined) setQuery(obj.query);
              flashResults();
            }
            if (obj.debug) {
              // Admin-only filter dump — insert just before the streaming
              // assistant bubble so it reads: you → [filter] → reply.
              setMessages((m) => {
                const copy = [...m];
                copy.splice(Math.max(copy.length - 1, 0), 0, { role: "debug", content: obj.debug });
                return copy;
              });
            }
            if (obj.tool) setStatus(obj.status || "Searching live award availability…");
            if (obj.note) {
              assistantText += (assistantText ? "\n\n" : "") + obj.note;
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: assistantText };
                return copy;
              });
            }
            if (obj.text) {
              assistantText += obj.text;
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: assistantText };
                return copy;
              });
              setStatus(null);
            }
            if (obj.done) setStatus(null);
            if (obj.paywall) {
              // The paywall is rendered as a footer below the chat thread,
              // not inserted into the message stream. Refresh /referrals/me
              // so the footer's `paywall.active` flag flips on.
              setStatus(null);
              loadReferrals();
            }
            if (obj.error) {
              setErr(obj.error);
              setStatus(null);
            }
          } catch {}
        }
      }
    } catch (e) {
      if (e.name !== "AbortError") setErr(String(e.message || e));
    } finally {
      setRunning(false);
      setStatus(null);
      abortRef.current = null;
      loadThreads(); // refresh titles / ordering
      loadReferrals(); // search may have unlocked us mid-turn
    }
  };

  const statusPill = (
    <div className="text-[11px] text-neutral-500 flex items-center gap-1.5 whitespace-nowrap">
      <span className={`w-2 h-2 rounded-full ${serverOk === null ? "bg-neutral-400" : serverOk ? "bg-emerald-600" : "bg-red-600"}`} />
      {serverOk === null ? "checking…" : serverOk ? "Concierge online" : "Concierge offline"}
    </div>
  );

  const Rail = (
    <div className="flex flex-col h-full">
      <div className="px-3 pt-4 pb-2 flex items-start justify-between gap-2">
        <h1 className="font-display font-bold tracking-tight text-[15px] leading-tight">
          Plan a trip with points
        </h1>
        <button
          onClick={newTrip}
          disabled={running}
          className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full text-neutral-700 hover:bg-black/[0.06] disabled:opacity-40 transition-colors"
          title="New trip"
          aria-label="New trip"
          data-testid="trip-new-btn"
        >
          <Plus size={16} />
        </button>
      </div>
      <div className="flex-1 overflow-auto px-2 py-2 space-y-0.5">
        {threads.length === 0 ? (
          <div className="text-[12px] text-neutral-400 px-2 py-3">No trips yet.</div>
        ) : (
          threads.map((t) => (
            <div
              key={t.id}
              className={`group flex items-start gap-1.5 rounded-[8px] px-2 py-1.5 cursor-pointer transition-colors ${
                t.id === paramId ? "bg-black/[0.06]" : "hover:bg-black/[0.03]"
              }`}
              onClick={() => { setRailOpen(false); navigate(`/plan/${t.id}`); }}
              data-testid={`trip-thread-${t.id}`}
            >
              <MessageSquare size={13} className="text-neutral-400 shrink-0 mt-0.5" />
              <span className="flex-1 text-[12.5px] text-neutral-700 leading-snug break-words line-clamp-3">{t.title}</span>
              <button
                onClick={(e) => { e.stopPropagation(); deleteThread(t.id); }}
                className="opacity-0 group-hover:opacity-100 text-neutral-400 hover:text-red-600 shrink-0 mt-0.5"
                aria-label="Delete trip"
                data-testid={`trip-thread-del-${t.id}`}
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))
        )}
      </div>
      <button
        onClick={() => { setRailOpen(false); navigate("/dashboard"); }}
        className="btn btn-ghost text-[13px] justify-start mx-3 mb-3 mt-1 border-t border-black/5 rounded-none pt-3"
        data-testid="trip-back"
      >
        <ArrowLeft size={14} /> Dashboard
      </button>
    </div>
  );

  return (
    <div className="h-screen flex flex-col bg-[#faf9f5] overflow-hidden">
      {/* Mobile: floating button to open the trips drawer (no top bar) */}
      <button
        onClick={() => setRailOpen(true)}
        className="md:hidden fixed top-3 left-3 z-40 inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/85 backdrop-blur border border-black/10 shadow text-neutral-700"
        aria-label="Open trips"
      >
        <Menu size={16} />
      </button>

      <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* Left rail (desktop) */}
        <aside className="hidden md:flex w-60 shrink-0 border-r border-black/5 bg-white/40 overflow-hidden">
          {Rail}
        </aside>

        {/* Mobile drawer */}
        {railOpen && (
          <div className="fixed inset-0 z-50 md:hidden" onClick={() => setRailOpen(false)}>
            <div className="absolute inset-0 bg-black/40" />
            <div className="absolute left-0 top-0 bottom-0 w-64 bg-[#faf9f5] border-r border-black/10 shadow-xl flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-end px-2 py-2 shrink-0">
                <button onClick={() => setRailOpen(false)} className="text-neutral-400 hover:text-black p-1" aria-label="Close">
                  <X size={16} />
                </button>
              </div>
              <div className="flex-1 min-h-0">{Rail}</div>
            </div>
          </div>
        )}

        {/* Main column */}
        <main className="flex-1 flex flex-col min-h-0 min-w-0 overflow-hidden">
          {/* Results panel (top) — sized to show all options without its own
              scroll; the chat below is the only scroll region. */}
          <div
            className={`shrink-0 px-4 md:px-6 py-4 border-b border-black/5 bg-white/30 transition-shadow duration-700 ${
              pulse ? "ring-2 ring-inset ring-emerald-400/70" : "ring-0 ring-transparent"
            }`}
            data-testid="trip-results-panel"
          >
            {flights.length > 0 ? (
              <FlightResults flights={flights} query={query} status={statusPill} />
            ) : !hasPrograms ? (
              <div className="text-center py-10" data-testid="trip-needs-programs">
                <div className="flex justify-center mb-3">{statusPill}</div>
                <Wallet size={22} className="mx-auto text-neutral-400 mb-3" />
                <div className="text-[15px] font-semibold text-neutral-800">
                  Add a loyalty program to plan a trip
                </div>
                <div className="text-[13px] text-neutral-600 mt-2 mb-4 max-w-md mx-auto leading-relaxed">
                  The Concierge plans against your actual balances. Connect
                  at least one airline or hotel program from your dashboard
                  and the planner will activate automatically.
                </div>
                <button
                  type="button"
                  onClick={() => navigate("/dashboard")}
                  className="btn-primary text-[13px] py-1.5 px-4"
                  data-testid="trip-go-to-dashboard"
                >
                  Go to dashboard
                </button>
              </div>
            ) : (
              <div className="text-center py-6">
                <div className="flex justify-center mb-3">{statusPill}</div>
                <Sparkles size={20} className="mx-auto text-neutral-400 mb-2" />
                <div className="text-[14px] font-semibold text-neutral-700">Describe your trip below</div>
                <div className="text-[12.5px] text-neutral-500 mt-1 mb-3">
                  The Concierge reads your real balances and surfaces the best award options — then keeps refining as you chat.
                </div>
                <div className="flex flex-wrap gap-1.5 justify-center">
                  {PROMPT_EXAMPLES.map((ex, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => send(ex)}
                      disabled={running}
                      className="text-[11.5px] px-2.5 py-1 border border-black/10 rounded-full text-neutral-600 hover:bg-black/[0.03] transition-colors"
                      data-testid={`trip-example-${i}`}
                    >
                      <Sparkles size={10} className="inline mr-1 text-neutral-400" />
                      {ex.length > 70 ? ex.slice(0, 67) + "…" : ex}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Chat thread */}
          <div ref={scrollRef} className="flex-1 overflow-auto px-4 md:px-6 py-4 space-y-2.5">
            {messages.length === 0 ? (
              <div className="text-[12.5px] text-neutral-400 text-center mt-6">
                Your conversation with the Concierge will appear here.
              </div>
            ) : (
              messages.map((m, i) => (
                m.role === "debug" ? (
                  <DebugFilter key={i} debug={m.content} />
                ) : (
                <Bubble key={i} role={m.role}>
                  {m.role === "assistant" ? (
                    m.content ? (
                      <div className="md" style={{ fontSize: "13px" }} dangerouslySetInnerHTML={{ __html: renderMarkdown(m.content) }} />
                    ) : running && i === messages.length - 1 ? (
                      <div className="text-neutral-500 italic text-[12.5px] flex items-center gap-1.5">
                        <span className="spinner" /> {status || "thinking…"}
                      </div>
                    ) : (
                      <div className="text-neutral-400 text-[12.5px]">See the updated results above.</div>
                    )
                  ) : (
                    <div className="whitespace-pre-wrap">{m.content}</div>
                  )}
                </Bubble>
                )
              ))
            )}
            {err && (
              <div
                className="px-3 py-2 text-[12px] rounded-[10px] border"
                style={{ background: "var(--bad-bg)", color: "var(--bad)", borderColor: "var(--bad-border)" }}
                data-testid="trip-error"
              >
                {err}
              </div>
            )}

            {/* Paywall footer — rendered after the last message, not inside
                the thread. Always visible while the user is past their
                free-context quota. Shape depends on the user's gating_mode:
                "subscription" → Subscribe CTA; "referral" (default) →
                invite-a-friend card. */}
            {referrals?.paywall?.active && (referrals?.gating_mode || "referral") === "subscription" && (
              <div
                className="mt-3 border border-amber-200 bg-amber-50/70 rounded-[12px] px-4 py-3.5"
                data-testid="trip-paywall-footer-subscription"
              >
                <div className="text-[13px] leading-relaxed text-neutral-900">
                  You've reached the limit of two complimentary routes
                  {referrals.paywall.used?.length
                    ? ` (${referrals.paywall.used.join(", ")})`
                    : ""}
                  . Subscribe to plan additional routes — your existing
                  searches and threads stay intact.
                </div>
                <button
                  type="button"
                  onClick={() => setSubscribeOpen(true)}
                  className="btn-primary text-[12.5px] py-1.5 px-3 mt-3"
                  data-testid="trip-paywall-subscribe"
                >
                  Subscribe
                </button>
              </div>
            )}
            {referrals?.paywall?.active && (referrals?.gating_mode || "referral") !== "subscription" && (() => {
              const pending = (referrals?.referrals || []).filter(
                (r) => r.status !== "qualified"
              );
              const hasPending = pending.length > 0;
              return (
                <div
                  className="mt-3 border border-amber-200 bg-amber-50/70 rounded-[12px] px-4 py-3.5"
                  data-testid="trip-paywall-footer"
                >
                  <div className="text-[13px] leading-relaxed text-neutral-900">
                    You've reached the limit of two complimentary routes
                    {referrals.paywall.used?.length
                      ? ` (${referrals.paywall.used.join(", ")})`
                      : ""}
                    .
                    {hasPending
                      ? " You have invitations in flight — planning will resume the moment any one of your invitees adds a loyalty program."
                      : " To plan additional routes, please invite a friend to join Reservoir."}
                  </div>

                  {hasPending && (
                    <div className="mt-3 border border-black/10 rounded-md bg-white divide-y divide-black/5" data-testid="trip-paywall-pending-list">
                      {pending.map((r) => {
                        const label =
                          r.status === "joined"
                            ? "Signed up — needs to add a loyalty program"
                            : "Invitation sent — waiting for sign-up";
                        return (
                          <div
                            key={r.id || r.referred_email}
                            className="flex items-start gap-2 px-3 py-2 text-[12.5px]"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-neutral-900 truncate">
                                {r.referred_name || r.referred_email}
                              </div>
                              <div className="text-[11.5px] text-neutral-500 truncate">
                                {r.referred_email} · {label}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {hasPending && (
                    <div className="text-[12.5px] text-neutral-700 mt-3 leading-relaxed">
                      Send them a reminder, or share your link with someone new:
                    </div>
                  )}

                  {referrals?.share_url && (
                    <div className="mt-2 flex items-center gap-2 border border-black/10 rounded-md bg-white px-2.5 py-1.5">
                      <span className="flex-1 truncate text-[12px] font-mono text-neutral-700">
                        {referrals.share_url}
                      </span>
                      <button
                        type="button"
                        onClick={copyReferralLink}
                        className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2 py-1 rounded-md bg-black text-white hover:bg-neutral-800 transition-colors"
                        data-testid="trip-paywall-copy-link"
                      >
                        {linkCopied ? (
                          <><Check size={11} /> Copied</>
                        ) : (
                          <><Copy size={11} /> Copy link</>
                        )}
                      </button>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => setInviteOpen(true)}
                    className="btn-primary text-[12.5px] py-1.5 px-3 mt-3"
                    data-testid="trip-paywall-invite"
                  >
                    {hasPending ? "Invite someone else" : "Invite a friend"}
                  </button>
                </div>
              );
            })()}
          </div>

          {/* Composer (hidden when the user has no loyalty programs — the
              planner has nothing to plan against). */}
          {hasPrograms ? (
            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="shrink-0 border-t border-black/5 bg-white/60 p-3 flex items-end gap-2"
            >
              <textarea
                rows={2}
                className="input resize-none flex-1 text-[13px]"
                placeholder="Describe a trip, or refine the results — e.g. 'make it business class' or 'a week earlier'…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                data-testid="trip-input"
                disabled={running}
              />
              {running ? (
                <button type="button" onClick={stop} className="btn btn-ghost text-[12.5px] px-3" data-testid="trip-stop-btn">
                  Stop
                </button>
              ) : (
                <button type="submit" disabled={!input.trim()} className="btn btn-primary px-3" data-testid="trip-submit-btn">
                  <Send size={14} />
                </button>
              )}
            </form>
          ) : (
            <div
              className="shrink-0 border-t border-black/5 bg-white/60 px-4 py-3 text-center text-[12px] text-neutral-500"
              data-testid="trip-composer-disabled"
            >
              Connect a loyalty program to start planning.
            </div>
          )}
        </main>
      </div>
      <InviteFriendModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSent={loadReferrals}
      />
      <SubscribeModal
        open={subscribeOpen}
        onClose={() => setSubscribeOpen(false)}
        onSuccess={loadReferrals}
      />
    </div>
  );
}
